/**
 * `pending-salesforce-accelerators` SF0 — preset catalogue.
 *
 * v0 ships **named smart-action presets** rather than a full
 * Salesforce step package. Each preset is a templated sequence of
 * canonical step types (click / type / wait) targetted at the
 * **Lightning Experience** DOM (the only SFDC UI we promise to
 * support in v0; Classic is explicitly out-of-scope).
 *
 * Presets are tagged `beta` — selector drift on quarterly SFDC
 * upgrades will break them; we don't promise correctness until a
 * design-partner customer signs and we promote SF0 → the full
 * `packages/accelerators-sfdc` package.
 *
 * Five presets land in v0 (the rest defer to v1):
 *   1. salesforce.login           — username + password + 2FA preflight
 *   2. salesforce.logout          — open user menu → "Log Out"
 *   3. salesforce.navigateToTab   — App Launcher → tab name
 *   4. salesforce.openObject      — global search → object → click row
 *   5. salesforce.createRecord    — "New" button → fill form → Save
 */

import { z } from 'zod';

export const SalesforcePresetIdSchema = z.enum([
  'salesforce.login',
  'salesforce.logout',
  'salesforce.navigateToTab',
  'salesforce.openObject',
  'salesforce.createRecord',
]);
export type SalesforcePresetId = z.infer<typeof SalesforcePresetIdSchema>;

export const SalesforcePresetMetaSchema = z.object({
  id: SalesforcePresetIdSchema,
  /** Human-readable name as it appears in the palette. */
  name: z.string(),
  /** One-line description. */
  description: z.string(),
  /** Beta marker — operators must explicitly opt in via admin-config. */
  beta: z.literal(true),
  /** Salesforce UI flavour. v0 only ships `lightning`. */
  uiFlavour: z.literal('lightning'),
  /** Required input variables the preset asks the user to fill in. */
  inputs: z.array(z.object({
    name: z.string(),
    label: z.string(),
    type: z.enum(['text', 'password', 'email', 'url']),
    required: z.boolean().default(true),
  })),
});
export type SalesforcePresetMeta = z.infer<typeof SalesforcePresetMetaSchema>;

export const SalesforcePresetStepSchema = z.object({
  type: z.string(),
  target: z.string().optional(),
  value: z.string().optional(),
  fields: z.record(z.string()).optional(),
  title: z.string(),
});
export type SalesforcePresetStep = z.infer<typeof SalesforcePresetStepSchema>;

export const ExpandSalesforcePresetBodySchema = z.object({
  presetId: SalesforcePresetIdSchema,
  inputs: z.record(z.string()).default({}),
});
export type ExpandSalesforcePresetBody = z.infer<typeof ExpandSalesforcePresetBodySchema>;

export const ExpandSalesforcePresetResponseSchema = z.object({
  steps: z.array(SalesforcePresetStepSchema),
  warnings: z.array(z.string()),
});
export type ExpandSalesforcePresetResponse = z.infer<typeof ExpandSalesforcePresetResponseSchema>;

export const ListSalesforcePresetsResponseSchema = z.object({
  items: z.array(SalesforcePresetMetaSchema),
});
export type ListSalesforcePresetsResponse = z.infer<typeof ListSalesforcePresetsResponseSchema>;

export const SalesforceAdminConfigSchema = z.object({
  /** Off by default — the presets only appear in the palette when this is true. */
  presetsEnabled: z.boolean().default(false),
  /**
   * Default SFDC instance URL (e.g. `https://acme.my.salesforce.com`).
   * Used as the base URL when the preset is expanded.
   */
  defaultInstanceUrl: z.string().url().or(z.literal('')).default(''),
}).strict();
export type SalesforceAdminConfig = z.infer<typeof SalesforceAdminConfigSchema>;
